import { MouseEvent, useCallback, useEffect, useState } from "react";
import ArrowIcon from "../../../../../assets/images/moreInfoArrow.svg";
import { ISeminarTask } from "../../../../../models/tasks";
import TaskBlock from "./TaskBlock";
import { useTranslation } from "react-i18next";
import taskInstructions from "../../../../../assets/images/taskInstructions.svg";
import styles from "./index.module.scss";
import { getFilteredBlocks } from "../../../../../utils/blockUtils";

interface IEventTask {
  eventId: string;
  task: ISeminarTask;
  language: string;
  allUserSelections?: { [taskId: string]: any[] };
}

export const EventTask = ({ task, eventId, language, allUserSelections }: IEventTask) => {
  const {
    Participant_Choices__r,
    Allow_Select_All__c,
    Min_Selection__c,
    Max_Selection__c,
    Status__c,
    Title__c,
    Task_Description__c,
    User_Instructions__c,
    Blocks__r,
    Type__c
  } = task;

  const [t] = useTranslation(language);
  const [taskOpen, setTaskOpen] = useState<boolean>(false);
  const [taskTags, setTaskTags] = useState([]);
  const [isTaskSelectingInProgress, setIsTaskSelectingInProgress] = useState<boolean>(false);
  const isNotEnglish = language !== "English";

  const isCompletedTask = useCallback(() => {
    const { records = [] } = Participant_Choices__r || {};

    if (Type__c === "Class Ranking") {
      return records[0]?.Selected_Option__c && records[0]?.Second_Option__c;
    }

    const participantChoices = records?.length || 0;
    return Allow_Select_All__c ?
      participantChoices >= Min_Selection__c :
      participantChoices > 0;
  }, [task]);

  const isPendingTask = Status__c === "Live" && !isCompletedTask();

  useEffect(() => {
    let tags = [];

    if (Status__c === "Completed") tags.push("closed");
    if (isCompletedTask()) tags.push("done");
    else if (Status__c === "Live" && !isCompletedTask()) tags = ["todo"];

    setTaskTags(tags);
  }, [task, isCompletedTask]);

  const openTask = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setTaskOpen(!taskOpen);
  };

  const getTaskStatusClass = () => {
    if (Status__c === "Completed") return styles.taskClosed;
    if (isCompletedTask()) return styles.taskCompleted;
    return styles.taskPending;
  }

  const blocksForTask = Blocks__r?.records || [];
  const filteredBlocks = Type__c === "Track Selection" ? getFilteredBlocks(blocksForTask, allUserSelections, task) : blocksForTask;

  return (
    <div className={`${styles.taskContainer} ${getTaskStatusClass()}`}>
      <div className={styles.taskContent}>
        <div className={styles.taskDetails}>
          <div className={styles.taskTitle}>
            {Title__c}
          </div>
          {Task_Description__c && (
            <div className={styles.taskDescription}>{Task_Description__c}</div>
          )}
        </div>
        <div className={styles.taskActions}>
          <div className={styles.taskTags}>
            {taskTags.map((tag) => (
              <div key={tag} className={`${styles.taskTag} ${styles[tag.split(" ").join("")]} ${isNotEnglish ? styles.isNotEnglish : ""}`}>
                {t(tag)}
              </div>
            ))}
          </div>
          <div className={styles.taskMoreInfo} onClick={(e) => openTask(e)}>
            <span className={isNotEnglish ? styles.inNotEnglish : ""}>{taskOpen ? t("lessInfo") : t("moreInfo")}</span>
            <img
              src={ArrowIcon}
              className={`${styles.moreInfoIcon} ${taskOpen ? styles.open : ""} `}
            />
          </div>
        </div>
      </div>

      {taskOpen && (
        <div className={`${styles.taskExtendedContainer} ${isPendingTask ? styles.isPendingTask : ""}`}>
          {Status__c === "Live" && User_Instructions__c && (
            <div className={styles.taskInstructions}>
              <img src={taskInstructions} alt="taskInstructions" className={`${isNotEnglish ? styles.notEnglish : ""}`} />
              <span>{User_Instructions__c}</span>
            </div>
          )}

          {Allow_Select_All__c && Participant_Choices__r?.records?.length > 0 && (
            <div className={styles.multiSelect}>
              <div>
                {Participant_Choices__r.records.length < Max_Selection__c ? (
                  <>
                    You can choose <strong>{Max_Selection__c - Participant_Choices__r.records.length}</strong> additional selections
                  </>
                ) : (
                  "You are done!"
                )}
              </div>
            </div>
          )}

          {filteredBlocks.map((block) => {
            return (
              <TaskBlock
                key={block.Id}
                taksRecord={task}
                block={block}
                userSelection={Participant_Choices__r}
                task={task.Id}
                eventId={eventId}
                status={Status__c}
                language={language}
                isTaskSelectingInProgress={isTaskSelectingInProgress}
                setIsTaskSelectingInProgress={setIsTaskSelectingInProgress}
                allUserSelections={allUserSelections}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};